'use client';

import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

import { CardAppointmentCancelSection } from './card-appointment-cancel-section.component';

import { Appointment } from '@/types/appointments.types';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { clientSanitizeInput } from '@/utils/client-sanitize-input';
import { currencyFormatter, getPaymentMethodName } from '@/utils/formatters/currency';
import { dateFormatter } from '@/utils/formatters/date';
import { phonePatientSac, visualPhonePatientSac } from '@/utils/consts/sac.consts';

interface AppointmentCardV2ExpandedProps {
  appointment: Appointment;
  cancellationNotice?: string;
  noLeadingHr?: boolean;
  showCancelButton?: boolean;
  onEditPatient?: () => void;
  onEditDate?: () => void;
}

export function AppointmentCardExpanded({
  appointment,
  cancellationNotice,
  noLeadingHr,
  showCancelButton = true,
  onEditPatient,
  onEditDate,
}: AppointmentCardV2ExpandedProps) {
  const instructions = appointment.items[0]?.procedure?.instructions;
  const sanitizedInstructions = clientSanitizeInput(instructions);

  const purchaseDate = dateFormatter(appointment.createdAt, 'DD/MM/YYYY [às] HH:mm');
  const paymentAmount = currencyFormatter(appointment.payment.amount);
  const paymentMethod = getPaymentMethodName(appointment.payment.paymentMethod);

  return (
    <div className="flex flex-col gap-4">
      {!noLeadingHr && <hr className="border-t border-gray-200 w-full" />}

      <div className="flex flex-col gap-1">
        <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-bold">
          Instruções de preparo
        </Typography>
        {sanitizedInstructions
          ? (
              <div
                className="text-gray-700 text-base font-normal"
                dangerouslySetInnerHTML={{ __html: sanitizedInstructions }}
              />
            )
          : (
              <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">
                Sem instruções de preparo
              </Typography>
            )}
      </div>

      <div className="flex flex-col gap-1">
        <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-bold">
          Detalhes da compra
        </Typography>
        <Typography>
          Compra em
          {' '}
          {purchaseDate}
        </Typography>
        <Typography>
          {paymentAmount}
          {' '}
          (
          {paymentMethod}
          )
        </Typography>
      </div>

      <div className="flex flex-col gap-1">
        <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-bold">Atenção</Typography>
        <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">
          A Medprev não é um plano de saúde e a rede de parceiros é independente. Informamos que, para fins de
          imposto de renda, a Nota Fiscal emitida pela Medprev não pode ser utilizada, uma vez que se refere a um
          serviço administrativo. Para obter a nota fiscal correspondente à parte do valor do serviço prestado,
          recomendamos que você solicite diretamente ao local onde os procedimentos foram realizados.
        </Typography>
        <Typography variant={TypographyVariant.BODY_TEXT} className="font-normal">
          Precisa de ajuda? Envie um e-mail para
          {' '}
          <Link href="mailto:sac@medprev.online" className="text-green-500 font-semibold">
            sac@medprev.online
          </Link>
          {' '}
          ou entre em contato através do WhatsApp
          {' '}
          <Link href={`tel:${phonePatientSac}`} className="text-green-500 font-semibold">
            {visualPhonePatientSac}
          </Link>
        </Typography>
      </div>

      <CardAppointmentCancelSection
        appointment={appointment}
        showCancelButton={showCancelButton}
        onEditPatient={onEditPatient}
        onEditDate={onEditDate}
      />

      {cancellationNotice && (
        <div className="flex items-start gap-3 bg-warning-100 border border-warning-300 rounded-2xl px-3 py-3">
          <Icon icon={faCircleExclamation} className="text-warning-600 text-xl flex-shrink-0 mt-0.5" />
          <Typography variant={TypographyVariant.BODY_TEXT} className="text-warning-800 font-semibold">
            {cancellationNotice}
          </Typography>
        </div>
      )}
    </div>
  );
}
